/**
 * SheetService
 *
 * Service for programmatically showing sheets with DOM container management.
 * Handles finding and using container elements based on various strategies:
 * - Direct HTMLElement reference (sheetContainerRef)
 * - Screen key lookup: finds <main> element (optionally via data-screen-key attribute)
 * - Fallback to body if no <main> element found
 */

import type { ReactNode } from 'react';
import { ExpauseSheet } from '../service/sheetImpl/ExpauseSheet';

/**
 * Configuration for showing a sheet via SheetService
 */
export interface SheetServiceConfig {
  /** Content to display in the sheet */
  content: ReactNode;

  /** Optional title for the sheet (can be a string or React element) */
  title?: ReactNode;

  /** Optional CTA buttons configuration */
  cta?: {
    buttons: Array<{
      text: string;
      onClick: () => void | Promise<void>;
      variant?: 'label' | 'primary' | 'danger';
      disabled?: boolean;
      /** Whether clicking this button should auto-close the sheet (default: true) */
      closeOnClick?: boolean;
      /** Whether to show loading indicator on this button */
      loading?: boolean;
      /** Optional `data-qa-id` override for this button (forwarded to CTAButtons). */
      qaId?: string;
    }>;
    orientation?: 'row' | 'column';
  };

  /**
   * Optional container reference strategies:
   * 1. Direct HTMLElement reference (e.g., sheetContainerRef.current)
   * 2. Screen key string (will look for element with data-screen-key attribute, then search for <main>)
   * 3. undefined/null (will search for <main> element, or use body as fallback)
   */
  container?: HTMLElement | string | null;

  /**
   * Optional screen key identifying which route opened this sheet.
   * Used to:
   * 1. Determine if back button should close the sheet or navigate away
   * 2. Find/create a dedicated container for this route (inserted into <main> element)
   * Note: data-screen-key attribute in DOM is optional - will search for <main> directly if not found
   */
  screenKey?: string;

  /** Force display as dialog (overrides auto detection) */
  forceDialog?: boolean;

  /** Force display as bottom sheet (overrides auto detection) */
  forceBottomSheet?: boolean;

  /** Optional callback when sheet is closed */
  onClose?: () => void;

  /** Whether clicking outside closes the sheet (default: true) */
  closeOnClickOutside?: boolean;

  /** Optional custom max height for bottom sheet (e.g., '75%', '400px') */
  maxHeight?: string;

  /** Optional fixed height for sheet (e.g., '500px', '80vh'). Overrides maxHeight and sets both height and maxHeight to the same value. */
  fixedHeight?: string;

  /** Optional custom width for dialog (e.g., '400px', '90vw') */
  width?: string;

  /** Optional custom max width for dialog only (e.g., '500px', '80vw') */
  maxWidth?: string;

  /** Whether content is scrollable (default: true) */
  scrollable?: boolean;

  /**
   * Whether the sheet can be dismissed via swipe-to-dismiss gesture (default: true).
   * When false, disables swipe-to-dismiss functionality on mobile but keeps overlay tap-to-close.
   * Useful for sheets containing components with their own scroll management (e.g., steppers).
   */
  dismissible?: boolean;

  /**
   * Optional stable identifier for this sheet. Forwarded to the rendered
   * surface to build its `data-qa-id` (`sheet-<qaId>`) so the qa-tester can
   * `wait_for` this specific sheet to open. Test-only; never affects rendering.
   */
  qaId?: string;
}

/**
 * Find or create a sheet container based on the screenKey
 */
const findOrCreateContainerByScreenKey = (screenKey: string): HTMLElement | null => {
  // Check if there's already a sheet container for this screenKey
  const containerId = `sheet-container-${screenKey}`;
  let sheetContainer = document.getElementById(containerId) as HTMLElement | null;

  if (sheetContainer) {
    console.log('📦 [SheetService] Reusing existing sheet container for screenKey:', screenKey);
    return sheetContainer;
  }

  // Create a new sheet container
  sheetContainer = document.createElement('div');
  sheetContainer.id = containerId;
  sheetContainer.setAttribute('data-sheet-container', screenKey);
  sheetContainer.style.position = 'fixed';
  sheetContainer.style.inset = '0';
  sheetContainer.style.pointerEvents = 'none';
  sheetContainer.style.zIndex = '100'; // High z-index to ensure sheet is above all other UI elements (e.g., TopBar with z-50)

  // Find the active route container (RouteStack sets data-route-active="true")
  // This container holds the currently visible route
  const activeRouteContainer = document.querySelector('[data-route-active="true"]') as HTMLElement | null;

  if (activeRouteContainer) {
    // Insert the sheet container as a child of the active route container
    activeRouteContainer.appendChild(sheetContainer);
    console.log('📦 [SheetService] Created new sheet container in active route for screenKey:', screenKey, sheetContainer);
  } else {
    // Fallback: Insert at body level if no active route container found
    document.body.appendChild(sheetContainer);
    console.log('📦 [SheetService] Created new sheet container at body level (no active route found) for screenKey:', screenKey, sheetContainer);
  }

  return sheetContainer;
};

/**
 * Find container element based on the container parameter
 */
const findContainer = (container?: HTMLElement | string | null, screenKey?: string): HTMLElement | null => {
  // Strategy 1: Direct HTMLElement reference
  if (container instanceof HTMLElement) {
    console.log('📦 [SheetService] Using direct HTMLElement container:', container);
    return container;
  }

  // Strategy 2: Screen key lookup (from container parameter)
  if (typeof container === 'string') {
    const element = document.querySelector(`[data-screen-key="${container}"]`) as HTMLElement | null;
    if (element) {
      console.log('📦 [SheetService] Found container by screen key:', container, element);
      return element;
    }
    console.warn('📦 [SheetService] No element found with data-screen-key:', container);
  }

  // Strategy 3: Auto-create container from screenKey
  if (screenKey) {
    const autoContainer = findOrCreateContainerByScreenKey(screenKey);
    if (autoContainer) {
      return autoContainer;
    }
  }

  // No container found - return null to render to document.body with fixed positioning
  // This ensures the sheet is positioned relative to the viewport, not a scrollable container
  console.log('📦 [SheetService] No container specified, rendering to document.body with fixed positioning');
  return null;
};

/**
 * SheetService - Imperative API for showing sheets with container management
 */
export const SheetService = {
  show: (config: SheetServiceConfig) => {
    const containerElement = findContainer(config.container, config.screenKey);

    ExpauseSheet.show({
      content: config.content,
      title: config.title,
      cta: config.cta,
      container: containerElement,
      screenKey: config.screenKey,
      forceDialog: config.forceDialog,
      forceBottomSheet: config.forceBottomSheet,
      onClose: config.onClose,
      closeOnClickOutside: config.closeOnClickOutside,
      maxHeight: config.maxHeight,
      fixedHeight: config.fixedHeight,
      width: config.width,
      maxWidth: config.maxWidth,
      scrollable: config.scrollable,
      dismissible: config.dismissible,
      qaId: config.qaId,
      // Show drag indicator if dismissible or if clicking outside should close the sheet
      dragIndicator: config.dismissible || config.closeOnClickOutside,
    });
  },

  /**
   * Close the topmost sheet
   */
  close: () => {
    ExpauseSheet.close();
  },

  /**
   * Close all sheets
   */
  closeAll: () => {
    ExpauseSheet.closeAll();
  },
};
